import { Dialog as DialogPrimitive } from "@base-ui/react";
import { X } from "lucide-react";
import { twMerge } from "tailwind-merge";
import type { ComponentProps } from "react";

export const DialogRoot = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export const DialogPortal = DialogPrimitive.Portal;

export interface DialogBackdropProps extends ComponentProps<
  typeof DialogPrimitive.Backdrop
> {}

export function DialogBackdrop({ className, ...props }: DialogBackdropProps) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-backdrop"
      className={twMerge(
        "fixed inset-0 z-50 bg-black/50",
        "data-starting-style:animate-in data-[starting-style]:fade-in-0",
        "data-ending-style:animate-out data-[ending-style]:fade-out-0",
        typeof className === "string" ? className : undefined,
      )}
      {...props}
    />
  );
}

export interface DialogPopupProps extends ComponentProps<
  typeof DialogPrimitive.Popup
> {}

export function DialogPopup({ className, ...props }: DialogPopupProps) {
  return (
    <DialogPrimitive.Popup
      data-slot="dialog-popup"
      className={twMerge(
        "fixed left-1/2 top-1/2 z-50 flex w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col gap-4",
        "rounded-xl border border-border bg-surface p-6 shadow-lg",
        "data-starting-style:animate-in data-[starting-style]:fade-in-0 data-[starting-style]:zoom-in-95",
        "data-ending-style:animate-out data-[ending-style]:fade-out-0 data-[ending-style]:zoom-out-95",
        typeof className === "string" ? className : undefined,
      )}
      {...props}
    />
  );
}

export interface DialogHeaderProps extends ComponentProps<"div"> {}

export function DialogHeader({ className, ...props }: DialogHeaderProps) {
  return (
    <div
      data-slot="dialog-header"
      className={twMerge("flex items-start justify-between gap-4", className)}
      {...props}
    />
  );
}

export interface DialogTitleProps extends ComponentProps<
  typeof DialogPrimitive.Title
> {}

export function DialogTitle({ className, ...props }: DialogTitleProps) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={twMerge("text-lg font-semibold text-foreground", typeof className === "string" ? className : undefined)}
      {...props}
    />
  );
}

export interface DialogDescriptionProps extends ComponentProps<
  typeof DialogPrimitive.Description
> {}

export function DialogDescription({
  className,
  ...props
}: DialogDescriptionProps) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={twMerge("text-sm text-foreground-subtle", typeof className === "string" ? className : undefined)}
      {...props}
    />
  );
}

export interface DialogContentProps extends ComponentProps<"div"> {}

export function DialogContent({ className, ...props }: DialogContentProps) {
  return (
    <div
      data-slot="dialog-content"
      className={twMerge("flex flex-col gap-4", className)}
      {...props}
    />
  );
}

export interface DialogFooterProps extends ComponentProps<"div"> {}

export function DialogFooter({ className, ...props }: DialogFooterProps) {
  return (
    <div
      data-slot="dialog-footer"
      className={twMerge("flex items-center justify-end gap-2", className)}
      {...props}
    />
  );
}

export interface DialogCloseButtonProps extends ComponentProps<
  typeof DialogPrimitive.Close
> {}

export function DialogCloseButton({
  className,
  ...props
}: DialogCloseButtonProps) {
  return (
    <DialogPrimitive.Close
      data-slot="dialog-close-button"
      aria-label="Fechar"
      className={twMerge(
        "inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors",
        "hover:bg-muted hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        typeof className === "string" ? className : undefined,
      )}
      {...props}
    >
      <X className="size-4" />
    </DialogPrimitive.Close>
  );
}
